import type { BlocklistStatus, ServerStatus, Severity } from '@bartizo/shared';
import type { ReactNode } from 'react';
import type { TrafficRisk } from '../lib/traffic-risk.js';

/**
 * A server's status (knowledge doc section 16), and an enrollment token's.
 *
 * The word carries the meaning and the colour only reinforces it, because roughly
 * one in twelve men cannot tell the red from the green — on a console whose whole
 * job is saying what is wrong, that is not a detail to leave to hue.
 */

const SERVER_TONE: Record<ServerStatus, string> = {
  active: 'badge-success',
  pending: 'badge-accent',
  offline: 'badge-danger',
  revoked: 'badge',
};

const SERVER_TITLE: Record<ServerStatus, string> = {
  active: 'Enrolled, approved and sending heartbeats',
  pending: 'Enrolled and waiting for an administrator to approve it',
  offline: 'No heartbeat for more than three minutes',
  revoked: 'Removed; its key no longer authenticates',
};

export function ServerStatusBadge({ status }: { status: ServerStatus }): ReactNode {
  return (
    <span className={`badge ${SERVER_TONE[status]}`} title={SERVER_TITLE[status]}>
      {status}
    </span>
  );
}

export type TokenState = 'active' | 'exhausted' | 'expired' | 'revoked';

const TOKEN_TONE: Record<TokenState, string> = {
  active: 'badge-success',
  exhausted: 'badge',
  expired: 'badge',
  revoked: 'badge-danger',
};

export function TokenStateBadge({ state }: { state: TokenState }): ReactNode {
  return <span className={`badge ${TOKEN_TONE[state]}`}>{state}</span>;
}

/**
 * An event's severity (knowledge doc section 6).
 *
 * Five levels on one scale, so the badge has to say which end it is at without
 * relying on hue alone — `info` and `critical` are both "a coloured pill" to
 * anybody reading it in greyscale. The word is the signal; the colour agrees.
 */
const SEVERITY_TONE: Record<Severity, string> = {
  info: 'badge-muted',
  low: 'badge-muted',
  medium: 'badge-accent',
  high: 'badge-warning',
  critical: 'badge-danger',
};

export function SeverityBadge({ severity }: { severity: Severity }): ReactNode {
  return <span className={`badge ${SEVERITY_TONE[severity]}`}>{severity}</span>;
}

/** The fleet blocklist's states (P11.6). */
export function BlocklistStatusBadge({ status }: { status: BlocklistStatus }): ReactNode {
  const look: Record<BlocklistStatus, [string, string]> = {
    active: ['badge-danger', 'Blocked'],
    protected: ['badge-success', 'Protected'],
    expired: ['badge-muted', 'Ended'],
    removed: ['badge-muted', 'Removed'],
  };
  const [tone, label] = look[status];
  return <span className={`badge ${tone}`}>{label}</span>;
}

/**
 * A top-list row's risk (lib/traffic-risk.ts), worded like a severity so the
 * two scales read alike. `none` is not drawn: a list of "none" badges is noise.
 */
const RISK_TONE: Record<Exclude<TrafficRisk, 'none'>, string> = {
  low: 'badge-muted',
  medium: 'badge-accent',
  high: 'badge-warning',
  critical: 'badge-danger',
};

export function RiskBadge({ risk, why }: { risk: TrafficRisk; why: string }): ReactNode {
  if (risk === 'none') return <span className="muted">—</span>;
  return (
    <span className={`badge ${RISK_TONE[risk]}`} title={why}>
      {risk}
    </span>
  );
}
